import { useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { ApiDescriptionSchema, type ApiDescriptionDto } from '@taqon/contracts';
import { useSearchParams } from 'react-router';
import { errorMessage, getData } from '../../shared/api/client';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { SearchFilterBar } from '../../shared/ui/filters';

export const API_DESCRIPTION = '/api/v1/platform/api-description';
const PAGE_SIZES = [25, 50, 100];

/** Saves the OpenAPI document as a file, exactly as the API published it. */
function download(description: ApiDescriptionDto): void {
  const blob = new Blob([JSON.stringify(description, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'taqon-ibms-openapi.json';
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * The API description (SDD §3.4 TD-03; FRD-INT-002): every operation the API serves, with the
 * permission it requires, and the OpenAPI 3.1 document to download. Generated by the API from its
 * own routes and the shared contracts, so it cannot drift from what is deployed. Read-only.
 *
 * The operation list is route metadata, not business data, and arrives whole: it is filtered here.
 * The filters live in the address, so a view survives a refresh.
 */
export function ApiDescriptionPage() {
  const [params, setParams] = useSearchParams();
  const area = params.get('area') ?? '';
  const text = params.get('q') ?? '';
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[1] ?? 50);

  const description = useQuery({
    queryKey: ['platform', 'api-description'],
    queryFn: () => getData(API_DESCRIPTION, ApiDescriptionSchema),
    staleTime: Infinity,
  });
  const operations = useMemo(() => description.data?.['x-operations'] ?? [], [description.data]);
  const areas = useMemo(
    () => [...new Set(operations.map((o) => o.tag))].sort((a, b) => a.localeCompare(b)),
    [operations],
  );
  const needle = text.trim().toLowerCase();
  const shown = operations.filter(
    (o) =>
      (area === '' || o.tag === area) &&
      (needle === '' ||
        o.path.toLowerCase().includes(needle) ||
        (o.permission ?? '').toLowerCase().includes(needle)),
  );
  const setFilter = (key: 'area' | 'q', value: string) => {
    const next = new URLSearchParams(params);
    if (value === '') next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
    setPage(0);
  };

  return (
    <Stack spacing={3}>
      <PageHeader
        title="API description"
        description="The operations of the TAQON Infinity API and the permission each one requires, as an OpenAPI 3.1 document generated by the API itself."
        actions={
          description.data !== undefined ? (
            <Button
              variant="outlined"
              onClick={() => {
                download(description.data);
              }}
            >
              Download OpenAPI
            </Button>
          ) : undefined
        }
      />
      <SearchFilterBar
        aria-label="Operation filters"
        label="Path or permission contains"
        value={text}
        onChange={(value) => {
          setFilter('q', value);
        }}
      >
        <TextField
          select
          size="small"
          label="Area"
          value={area}
          onChange={(e) => {
            setFilter('area', e.target.value);
          }}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All areas</MenuItem>
          {areas.map((a) => (
            <MenuItem key={a} value={a}>
              {a}
            </MenuItem>
          ))}
        </TextField>
      </SearchFilterBar>
      <Section title="Operations">
        {description.isError && <ErrorState message={errorMessage(description.error)} />}
        {description.isPending && <LoadingState label="Loading the API description" />}
        {description.data !== undefined && shown.length === 0 && (
          <EmptyState title="No operations match" />
        )}
        {shown.length > 0 && (
          <>
            <Table size="small" aria-label="API operations">
              <TableHead>
                <TableRow>
                  <TableCell>Method</TableCell>
                  <TableCell>Path</TableCell>
                  <TableCell>Required permission</TableCell>
                  <TableCell>Request</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shown.slice(page * pageSize, (page + 1) * pageSize).map((o) => (
                  <TableRow key={`${o.method} ${o.path}`}>
                    <TableCell>
                      <Chip size="small" label={o.method.toUpperCase()} variant="outlined" />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      {o.path}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>
                      {o.permission ?? (
                        <Typography variant="body2" color="text.secondary" component="span">
                          Public (sign-in, session or health)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {[o.requestBody ? 'body' : null, o.queryParameters ? 'query' : null]
                        .filter((v) => v !== null)
                        .join(', ') || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={shown.length}
              page={page}
              rowsPerPage={pageSize}
              rowsPerPageOptions={PAGE_SIZES}
              onPageChange={(_, next) => {
                setPage(next);
              }}
              onRowsPerPageChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(0);
              }}
            />
          </>
        )}
      </Section>
    </Stack>
  );
}
